import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { inject } from "@vercel/analytics"
import { injectSpeedInsights } from "@vercel/speed-insights"
import React, { Suspense } from "react"
import ReactDOM from "react-dom/client"
import App from "./App.tsx"
import { storeToken, storeUser } from "./hooks/use-auth"
import { AuthError } from "./lib/errors"
import { homeFinancialSDK } from "./sdk/sdk"
import "./styles/globals.css"

injectSpeedInsights()
inject()

let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

async function attemptTokenRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const response = await homeFinancialSDK.users.refreshToken()
      if (response.token?.token) {
        storeToken(response.token.token)
        if (response.user) {
          storeUser(response.user)
        }
        return true
      }
      return false
    } catch {
      return false
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

let queryClient: QueryClient

async function handle401Error() {
  const refreshed = await attemptTokenRefresh()
  if (!refreshed) {
    localStorage.removeItem("auth_token")
    localStorage.removeItem("user_data")
    localStorage.removeItem("current_account")
    queryClient.clear()
    window.location.href = "/login"
  }
}

const mutationCache = new MutationCache({
  onError: (error) => {
    if (error instanceof AuthError && error.statusCode === 401) {
      handle401Error()
    }
  },
})

const queryCache = new QueryCache({
  onError: (error) => {
    if (error instanceof AuthError && error.statusCode === 401) {
      handle401Error()
    }
  },
})

queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
  mutationCache,
  queryCache,
})

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
)

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<LoadingFallback />}>
        <App />
      </Suspense>
    </QueryClientProvider>
  </React.StrictMode>
)
